import { BoxPage } from '@/components/common/BoxPage'
import CompanySelection from '@/components/selection/CompanySelection'
import FilterMonthAttendance from '@/components/attendance/detail-attendance/FillterMonthAttendance'
import { useNotification, useRouteQuery, useTranslator } from '@/hooks'
import { Card, CardContent, Divider, Typography, IconButton } from '@mui/material'
import { useState, useMemo, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@/@core/components/mui/Button'
import { useQuery } from '@tanstack/react-query'
import { ActivityLogTypeEnum, Attendances, AttendanceStatusEnum, Company } from '@/@data'
import { DetailAttendanceCompanyTable } from '@/components/attendance/tables/DetailAttendanceCompanyTable'
import { TableLimit } from '@/@core/components/mui/TableLimit'
import { InputField } from '@/components/common/input/InputField'
import { ReconciledButton } from '@/components/attendance/detail-attendance/ReconciledButton'
import { SettingCompanyAttendance } from '@/components/attendance/detail-attendance/SettingCompanyAttendance'
import { CompanyManualReconcile } from '@/components/attendance/detail-attendance/CompanyManualReconcile'
import { UploadCompanyAttendanceCVS } from '@/components/attendance/dialog/UploadCompanyAttendanceCVS'
import AppLogs from '@/components/common/AppLogs'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

dayjs.extend(isBetween)

const AttendanceDetail = () => {
  const { __ } = useTranslator()
  const { updateQuery, searchQuery } = useRouteQuery()
  const [isSettingCompanyAttendance, setIsSettingCompanyAttendance] = useState(false)
  const [manualReconcile, setManualReconcile] = useState(false)
  const [openUploadCompanyAttendanceCVS, setOpenUploadCompanyAttendanceCVS] = useState(false)
  const [isOpenLogs, setIsOpenLogs] = useState(false)
  const [currentPayPeriod, setCurrentPayPeriod] = useState(null)

  const n = useNotification()

  const { id } = useParams<{ id: string }>()

  const params = useMemo(() => {
    return {
      companyId: Number(searchQuery.get('companyId')) || Number(id),
      month: searchQuery.get('month')
    }
  }, [id, searchQuery.get('month'), searchQuery.get('companyId')])

  const { data, refetch: refetchAttendanceDetail } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(params),
    enabled: (!!id && !!searchQuery.get('month')) || !!searchQuery.get('companyId')
  })

  const paramsCompanyBeforeMonth = {
    companyId: Number(searchQuery.get('companyId')) || Number(id),
    month: dayjs(searchQuery.get('month')).subtract(1, 'month').format('YYYY-MM-DD')
  }

  const { data: dataCompanyBeforeMonth, refetch: refetchCompanyBeforeMonth } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(paramsCompanyBeforeMonth),
    // @ts-ignore
    enabled:
      !!Number(searchQuery.get('companyId')) ||
      (Number(id) && !!dayjs(searchQuery.get('month')).subtract(1, 'month').format('YYYY-MM-DD'))
  })

  useEffect(() => {
    const companyId = searchQuery.get('companyId')

    if (!companyId && data?.data?.current_pay_period) {
      setCurrentPayPeriod(data.data.current_pay_period)
      updateQuery({ month: data.data.current_pay_period })
    }
  }, [data?.data?.current_pay_period, searchQuery.get('companyId')])

  const paramsEmployee = useMemo(() => {
    return {
      companyId: Number(searchQuery.get('companyId')) || Number(id),
      month: searchQuery.get('month'),
      user_keyword: searchQuery.get('user_keyword'),
      page: searchQuery.get('page'),
      perPage: searchQuery.get('perPage')
    }
  }, [id, searchQuery])

  const { data: dataEmployee, refetch: refetchEmployee } = useQuery({
    ...Attendances.getAttendanceByCompanyIdEmployee(paramsEmployee),
    enabled: (!!id && !!searchQuery.get('month')) || !!searchQuery.get('companyId')
  })

  const handleSettingCompanyAttendance = () => {
    setIsSettingCompanyAttendance(true)
  }

  const { data: company, refetch: refetchCompany } = useQuery({
    ...Company.getCompanyById(Number(searchQuery.get('companyId')) || Number(id)),
    enabled: !!id || !!searchQuery.get('companyId')
  })

  const handleDownloadTable = async () => {
    try {
      const companyName = company?.data?.name || 'Company'
      const month = searchQuery.get('month') || 'Unknown'
      const fileName = `attendance_${companyName}_${month}.xlsx`

      await Attendances.exportAttendanceByCompanyIdEmployee(paramsEmployee, fileName)
    } catch (error) {
      n.error(__('Download failed'))
    }
  }

  return (
    <>
      {!manualReconcile && (
        <BoxPage
          showBackButton={true}
          title={__('Company Attendance Detail')}
          breadcrumbs={[
            { label: __('Attendance Management'), href: '/cms/attendance' },
            { label: __('Company Attendance Detail') }
          ]}
        >
          {!isSettingCompanyAttendance && (
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ padding: 0 }}>
                <div className='flex justify-between items-center p-5'>
                  <div className='flex items-center gap-2'>
                    <div className='w-[240px]'>
                      <CompanySelection
                        value={searchQuery.get('companyId') || id || ''}
                        onChange={(value, selected) => {
                          setCurrentPayPeriod(selected?.current_pay_period)
                          updateQuery({ companyId: value, page: 1, month: undefined })
                        }}
                        onClear={() => {
                          setCurrentPayPeriod(null)
                          updateQuery({ companyId: undefined, page: 1, month: undefined })
                        }}
                      />
                    </div>
                    <Divider orientation='vertical' flexItem className='w-[2px] bg-[#2f2b3d1f]' />
                    <Typography variant='h6' className='text-[18px] font-medium'>
                      {__('Attendance Detail')}
                    </Typography>
                  </div>
                  <Button
                    variant='tonal'
                    color='primary'
                    size='small'
                    startIcon={<i className={`tabler-download`} />}
                    onClick={handleDownloadTable}
                  >
                    {__('Download Table')}
                  </Button>
                </div>
                <Divider />
                <div className='p-5'>
                  <FilterMonthAttendance
                    key={`${searchQuery.get('companyId') || id}-${currentPayPeriod}`}
                    onDateChange={month => {
                      updateQuery({ month })
                    }}
                    status={data?.data?.status || ''}
                    data={data?.data || {}}
                    isShowWorkingRate={true}
                    isShowSettingDay={true}
                    currentPayPeriod={currentPayPeriod || data?.data?.current_pay_period}
                    isDisablePreviousMonth={!dataCompanyBeforeMonth}
                  />
                </div>
                <Divider />

                <div className='p-5 flex justify-between items-center'>
                  <div className='flex items-center gap-3'>
                    <div className='w-[80px]'>
                      <TableLimit />
                    </div>
                    <div className='flex items-center gap-3'>
                      <div className='flex items-center gap-1'>
                        <div className='w-[16px] h-[16px] rounded-full bg-[#577dfd]'></div>
                        <Typography variant='body2' className='text-[15px] font-medium text-[#2f2b3de6]'>
                          {__('Working Day')}
                        </Typography>
                      </div>
                      <div className='flex items-center gap-1'>
                        <div className='w-[16px] h-[16px] rounded-full bg-[#ff7074]'></div>
                        <Typography variant='body2' className='text-[15px] font-medium text-[#2f2b3de6]'>
                          {__('Absent Day')}
                        </Typography>
                      </div>
                      <div className='flex items-center gap-1'>
                        <div className='w-[16px] h-[16px] rounded-full bg-[#c0bec5]'></div>
                        <Typography variant='body2' className='text-[15px] font-medium text-[#2f2b3de6]'>
                          {__('Non-Working Day')}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='w-[240px]'>
                      <InputField
                        placeholder={__('Search user')}
                        onChange={e => {
                          updateQuery({ user_keyword: e.target.value, page: 1 })
                        }}
                        className='w-full'
                      />
                    </div>
                    <ReconciledButton
                      manualReconcile={manualReconcile}
                      setManualReconcile={setManualReconcile}
                      setOpenUploadCompanyAttendanceCVS={setOpenUploadCompanyAttendanceCVS}
                      isDisabled={data?.data?.status === AttendanceStatusEnum.Reconciled}
                    />
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={handleSettingCompanyAttendance}
                    >
                      <i className='tabler-settings text-[#2f2b3de6]' style={{ fontSize: 18 }} />
                    </IconButton>

                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={() => {
                        setIsOpenLogs(true)
                      }}
                    >
                      <i className='tabler-history text-[#2f2b3de6]' style={{ fontSize: 18 }} />
                    </IconButton>
                  </div>
                </div>
                <Divider />

                <DetailAttendanceCompanyTable
                  data={dataEmployee?.data || []}
                  dataNormal={data?.data || {}}
                  refetch={() => {
                    refetchEmployee()
                    refetchAttendanceDetail()
                    refetchCompanyBeforeMonth()
                  }}
                  meta={dataEmployee?.meta || null}
                />
              </CardContent>
            </Card>
          )}

          {isSettingCompanyAttendance && (
            <SettingCompanyAttendance
              setIsSettingCompanyAttendance={setIsSettingCompanyAttendance}
              company={company?.data}
            />
          )}
        </BoxPage>
      )}

      {manualReconcile && (
        <CompanyManualReconcile
          company={company?.data}
          setManualReconcile={setManualReconcile}
          onSuccess={() => {
            refetchEmployee()
            refetchAttendanceDetail()
            refetchCompanyBeforeMonth()
            refetchCompany()
          }}
        />
      )}

      {openUploadCompanyAttendanceCVS && (
        <UploadCompanyAttendanceCVS
          open={openUploadCompanyAttendanceCVS}
          onClose={() => {
            setOpenUploadCompanyAttendanceCVS(false)
            refetchEmployee()
            refetchAttendanceDetail()
            refetchCompanyBeforeMonth()
            refetchCompany()
          }}
          onComplete={() => {
            setOpenUploadCompanyAttendanceCVS(false)
            refetchEmployee()
            refetchAttendanceDetail()
            refetchCompany()
            refetchCompanyBeforeMonth()
            setManualReconcile(true)
          }}
        />
      )}

      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Attendance')}
        object_type={ActivityLogTypeEnum.Attendance}
        objectId={id || searchQuery.get('companyId')}
      />
    </>
  )
}

export default AttendanceDetail
